import React from 'react'
import type { ComponentType } from 'react'
import type { OrderCodeLinkProps } from '@ztechapps/webui.contract'
import type { PivotCellRenderer, PivotCellTextRenderer } from '../constants/types'
import { parsePivotLinkLabel } from './parsePivotLinkLabel'

/** Params for `renderOrderCodeCell`. */
type RenderOrderCodeCellProps = {
  OrderCodeLink: ComponentType<OrderCodeLinkProps>
  field: string
}

/**
 * Builds a pivot cell renderer for one `<code>|<_id>` order-reference field, around an
 * already-resolved `OrderCodeLink` (`WebuiIocKey.order.orderCodeLink`) — the caller resolves it
 * via `useInjectComponent` so this stays a plain function, not a hook. The wrapping span carries
 * the underline because the admin theme strips it from every anchor.
 */
export const renderOrderCodeCell =
  ({ OrderCodeLink, field }: RenderOrderCodeCellProps): PivotCellRenderer =>
  ({ field: cellField, label }) => {
    if (cellField !== field) return undefined
    const parsed = parsePivotLinkLabel(label)
    if (!parsed) return undefined
    return (
      <span className="text-decoration-underline">
        <OrderCodeLink {...parsed} target="_blank" />
      </span>
    )
  }

/** The copy-text twin of `renderOrderCodeCell` — the order code the cell displays. */
export const renderOrderCodeCellText =
  (field: string): PivotCellTextRenderer =>
  ({ field: cellField, label }) =>
    cellField === field ? parsePivotLinkLabel(label)?.code : undefined
